/**
 * 全站日期/时间展示工具。
 *
 * 统一采用「斜杠 + 无零填充 + 带秒」的 zh-CN 风格：
 *   - formatDateTime:  '2026/7/8 11:08:31'
 *   - formatDateOnly:  '2026/7/9'
 *
 * 设计约束：
 * - 同一页面内「下发时间」与「反馈日期」两个字段并列展示时，风格必须一致
 *   （日期部分同为 `YYYY/M/D`，无零填充、斜杠分隔）。
 * - 由后端下发的截止日期字段是纯 `YYYY-MM-DD` 字符串（不带时区/时间语义），
 *   为避免走 `new Date('2026-07-09')` 因 UTC 解析导致的偏移一天问题，
 *   `formatDateOnly` 支持直接接收 `YYYY-MM-DD` 字符串，走字符串拆分而非 Date。
 */

/**
 * 格式化日期时间：`YYYY/M/D H:mm:ss`
 *
 * 与 `toLocaleString('zh-CN', { hour12: false })` 的默认输出保持一致，
 * 但显式手写实现，避免不同浏览器 Intl 实现差异（如 Safari 会插入不可见字符）。
 */
export const formatDateTime = (iso: string | Date | null | undefined): string => {
  if (!iso) return '-';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return typeof iso === 'string' ? iso : '-';
  const pad = (n: number) => String(n).padStart(2, '0');
  // 年/月/日部分无零填充，时/分/秒部分保留零填充（否则 '11:8:31' 视觉太乱）
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}:${pad(d.getSeconds())}`;
};

/**
 * 格式化纯日期（无时间）：`YYYY/M/D`
 *
 * 入参可为：
 *  - `YYYY-MM-DD` 字符串（后端 DATE 字段常见形态，走字符串拆分，避免时区偏移）
 *  - ISO datetime 字符串 / Date 对象（走 Date 解析）
 *  - null / undefined / 空串（返回 '-'）
 */
export const formatDateOnly = (input: string | Date | null | undefined): string => {
  if (!input) return '-';

  // 纯 'YYYY-MM-DD' 字符串：直接拆分，避免 new Date('2026-07-09') 按 UTC 0 点解析导致
  // 东八区展示回退一天的问题。
  if (typeof input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
    const [y, m, d] = input.split('-');
    return `${y}/${Number(m)}/${Number(d)}`;
  }

  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return typeof input === 'string' ? input : '-';
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
};
